import CloseIcon from '@mui/icons-material/Close';
import { Popover } from '@mui/material';
import { IMaterialToAccount, IRiskTolerance } from '@sp-types/accountTypes';
import { ISupplierExposure } from '@sp-types/FutureOrderType';
import { Material } from '@sp-types/materialType';
import React, { useEffect, useState } from 'react';
import { useAccountStore } from '../../../../components/Stores/accountStore';
import { LinearGauge } from '../../../../components/UI-Elements/Charts/LinearGauge/LinearGauge';
import { MaterialDataCard } from '../../../../components/UI-Elements/Grids/MaterialDataGrid';
import { useExposure } from '../../../../hooks/useExposure';
import { formatToPriceInDollars, formatToShortString } from '../../../../utils/FormatValue';
import { MonthYearValue } from '../../../../utils/MonthYear/MonthYearValue';
import { getExposureColor, getExposureStatus } from '../../../../utils/RiskUtils';
import { getAdjustedMaterialsExposure } from './materialBreakdownSummeryUtils';
import { useExposureStore } from '../../../../components/Stores/exposureDataStore';
import { getUnitObj } from '../../../../utils/MassUnits/massUnitsUtils';
import { useCurrencyRates } from '@hooks/useCurrencyRates';

interface IProductOverviewMaterialBreakdownProps {
  totalQuantity: number;
  materialExposureData: ISupplierExposure[];
  materialTotalExposure?: { materialId: string; exposureCalendar: MonthYearValue[] }[];
  materialsMap: Record<string, Material>;
  materialColorDictionary: Record<string, string>;
  materialsToAccount: IMaterialToAccount[];
}
export const MaterialBreakdownSummary: React.FC<IProductOverviewMaterialBreakdownProps> = ({
  totalQuantity,
  materialTotalExposure,
  materialExposureData,
  materialsMap,
  materialColorDictionary,
  materialsToAccount,
}) => {
  const [materialsUpdatedExposure, setMaterialsUpdatedExposure] = useState<Record<string, number>>();
  const { account } = useAccountStore();
  const { data, refetch: refetchExpo } = useExposure(account.id);
  const { netExposure } = useExposureStore();
  const { convertionMap, pricesMap } = useCurrencyRates();

  React.useEffect(() => {
    if (account.id) {
      refetchExpo();
    }
  }, [account.id]);

  useEffect(() => {
    if (data && materialTotalExposure) {
      setMaterialsUpdatedExposure(
        getAdjustedMaterialsExposure(materialTotalExposure, netExposure, materialsMap, convertionMap),
      );
    }
  }, [data, materialTotalExposure]);

  return (
    <div className="flex flex-col max-h-full overflow-auto">
      <h6 className="text-red text-lg">Materials</h6>
      {materialsUpdatedExposure && (
        <MaterialDataCard
          materialColorDictionary={materialColorDictionary}
          materialsMap={materialsMap}
          materialsData={materialExposureData.map((item, index) => ({
            materialId: item.materialId,
            materialNameSize: 100,
            data: [
              {
                title: 'Quantity',
                valueAsString:
                  formatToShortString(item.weightInProduct * totalQuantity, 0) +
                  getUnitObj(materialsMap[item.materialId].unit).shortForm,
              },
              {
                title: 'Material Price',
                valueAsString: formatToPriceInDollars(pricesMap[item.materialId]),
              },
              {
                title: 'Total expected price',
                valueAsString: formatToPriceInDollars(
                  item.weightInProduct * totalQuantity * pricesMap[item.materialId],
                ),
              },

              <NetExposureValueData
                key={index}
                netExposure={materialsUpdatedExposure?.[item.materialId] || 0}
                riskTolerance={materialsToAccount.find((item) => item.materialId === item.materialId)?.RiskTolerance}
                materialName={materialsMap[item.materialId].name}
              />,
            ],
          }))}
        />
      )}
    </div>
  );
};

interface INewExposureProps {
  netExposure?: number;
  riskTolerance?: IRiskTolerance;
  materialName: string;
}

const NetExposureValueData: React.FC<INewExposureProps> = ({ netExposure, riskTolerance, materialName }) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const exposureColor =
    riskTolerance && netExposure ? getExposureColor(getExposureStatus(riskTolerance, netExposure)) : 'black';

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);
  const id = open ? 'simple-popover' : undefined;

  return (
    <div className="flex gap-14 overflow-visible">
      <div className="flex flex-col gap-[0.35rem]">
        <p className="text-gray-600 text-sm whitespace-nowrap">Net Exposure</p>
        <p style={{ color: exposureColor }} className="content-center text-lg whitespace-nowrap">
          {formatToPriceInDollars(netExposure || 0)}
        </p>
      </div>
      <button onClick={handleClick}>
        <img className="h-7 w-7 my-auto" src="/assets/icons/blue-flat/gauge.svg" alt="Balance Bar Icon" />
      </button>
      {riskTolerance && (
        <Popover
          id={id}
          open={open}
          anchorEl={anchorEl}
          onClose={handleClose}
          anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
          PaperProps={{ style: { backgroundColor: 'transparent', boxShadow: 'none' } }}
        >
          <div className="p-2 ">
            <div className="min-w-[25rem] min-h-[10rem] p-3 space-y-5 relative overflow-visible bg-white shadow-custom rounded-md">
              <div className="flex justify-between">
                <h6>{materialName} exposure</h6>
                <button onClick={() => handleClose()} className="mb-auto">
                  <CloseIcon className="text-purple" />
                </button>
              </div>
              <div className="px-2">
                <LinearGauge currentValue={netExposure || 0} riskTolerance={riskTolerance} />
              </div>
            </div>
          </div>
        </Popover>
      )}
    </div>
  );
};
